<template>
  <div class="home-page">
    <!-- Advertisement Carousel -->
    <section class="advertisements-section">
      <BookAdCarousel />
    </section>

    <!-- Main Content with Sidebar -->
    <div class="home-content">
      <!-- Sidebar -->
      <HomeSidebar 
        @category-filter="handleCategoryFilter"
        @author-filter="handleAuthorFilter"
      />
      
      <!-- Main Content Area -->
      <div class="main-content">
        <section class="hero-section">
          <div class="hero-content">
            <h1 class="hero-title">{{ $t('home.heroTitle') }}</h1>
            <p class="hero-subtitle">
              {{ $t('home.heroSubtitle') }}
            </p>
            <div class="hero-actions">
              <router-link to="/books" class="btn btn-primary hero-btn">
                {{ $t('home.browseLibrary') }}
              </router-link>
              <router-link to="/subscription" class="btn btn-secondary hero-btn">
                {{ $t('home.viewPlans') }}
              </router-link>
            </div>
          </div>
          <div class="hero-image">
            <img src="@/assets/oqu1024blue.png" alt="Digital Reading" />
          </div>
        </section>

        <section class="featured-books">
          <h2 class="section-title">{{ $t('home.featuredBooks') }}</h2>

          <div v-if="loading" class="loading-indicators">
            <div class="skeleton-card" v-for="i in 4" :key="i"></div>
          </div>

          <div v-else-if="error" class="error-state">
            <div class="error-message">
              <i class="pi pi-exclamation-triangle"></i>
              <p>{{ error }}</p>
            </div>
            <button @click="fetchFeaturedBooks" class="btn btn-primary retry-btn">
              <i class="pi pi-refresh"></i> {{ $t('home.retry') }}
            </button>
          </div>

          <div v-else-if="!isOnline" class="offline-state">
            <div class="offline-message">
              <i class="pi pi-wifi"></i>
              <p>{{ $t('home.offlineMessage') }}</p>
            </div>
          </div>

          <div v-else-if="featuredBooks.length === 0" class="no-books-message">
            {{ $t('home.noFeaturedBooks') }}
          </div>

          <div v-else class="featured-grid">
            <book-card v-for="book in featuredBooks" :key="book.id" :book="book" @click="viewBookDetails(book.id)" />
          </div>

          <div class="view-all-link">
            <router-link to="/books" class="btn btn-secondary">
              {{ $t('home.viewAllBooks') }}
            </router-link>
          </div>
        </section>

        <section class="features-section">
          <h2 class="section-title">{{ $t('home.whyChooseUs') }}</h2>

          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="pi pi-desktop"></i>
              </div>
              <h3 class="feature-title">{{ $t('home.readAnywhere') }}</h3>
              <p class="feature-description">
                {{ $t('home.readAnywhereDesc') }}
              </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <i class="pi pi-bookmark"></i>
              </div>
              <h3 class="feature-title">{{ $t('home.bookmarksNotes') }}</h3>
              <p class="feature-description">
                {{ $t('home.bookmarksNotesDesc') }}
              </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <i class="pi pi-book"></i>
              </div>
              <h3 class="feature-title">{{ $t('home.extensiveLibrary') }}</h3>
              <p class="feature-description">
                {{ $t('home.extensiveLibraryDesc') }}
              </p>
            </div>
          </div>
        </section>

        <section class="pricing-cta">
          <div class="cta-content">
            <h2 class="cta-title">{{ $t('home.readyToStart') }}</h2>
            <p class="cta-description">
              {{ $t('home.readyToStartDesc') }}
            </p>
            <router-link to="/subscription" class="btn btn-primary">
              {{ $t('home.viewSubscriptionPlans') }}
            </router-link>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { checkNetworkStatus, addNetworkListeners } from '@/utils/network';
import { useRouter } from 'vue-router';
import BookCard from '@/components/books/BookCard.vue';
import BookAdCarousel from '@/components/BookAdCarousel.vue';
import HomeSidebar from '@/components/layout/HomeSidebar.vue';
import api from '@/services/api';

export default defineComponent({
  name: 'HomeView',
  components: {
    BookCard,
    BookAdCarousel,
    HomeSidebar
  },
  setup() {
    const router = useRouter();
    const featuredBooks = ref([]);
    const loading = ref(true);
    const error = ref(false);

    const isOnline = ref(checkNetworkStatus());
    
    const handleOnline = () => {
      isOnline.value = true;
      // Retry fetching data that failed
      if (error.value) {
        fetchFeaturedBooks();
      }
    };
    
    const handleOffline = () => {
      isOnline.value = false;
    };
    
    // Setup network listeners
    const removeListeners = addNetworkListeners(handleOnline, handleOffline);
    
    onMounted(() => {
      fetchFeaturedBooks();
    });
    
    onUnmounted(() => {
      // Clean up listeners
      removeListeners();
    });

    const fetchFeaturedBooks = async () => {
      if (!isOnline.value) {
        error.value = 'You are offline. Please check your connection.';
        return;
      }

      loading.value = true;
      error.value = null; // Reset error state

      try {
        const response = await api.getBooks({ is_featured: true, page_size: 4 });
        featuredBooks.value = response.data.results || [];
      } catch (err) {
        console.error('Component error fetching books:', err.message);
        error.value = 'Unable to load books. Please try again later.';
        featuredBooks.value = []; // Reset to empty array on error
      } finally {
        loading.value = false;
      }
    };

    const viewBookDetails = (bookId) => {
      router.push({ name: 'book-detail', params: { id: bookId } });
    };

    const handleCategoryFilter = () => {
      // You can add additional logic here if needed
    };

    const handleAuthorFilter = () => {
      // You can add additional logic here if needed
    };

    onMounted(fetchFeaturedBooks);

    return {
      isOnline,
      featuredBooks,
      loading,
      error,
      viewBookDetails,
      handleCategoryFilter,
      handleAuthorFilter,
    };
  }
});
</script>

<style lang="scss" scoped>
.home-page {
  max-width: 100%;
  margin: 0 auto;
}

.home-content {
  display: flex;
  max-width: 1440px;
  margin: 0 auto;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 1rem;
  }
}

.main-content {
  flex: 1;
  min-width: 0; // Prevents flex item from overflowing
}

.hero-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4rem 0;
  gap: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem 0;
  }
}

.hero-content {
  flex: 1;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-color);
  margin-bottom: 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.6;
  font-family: 'Alkatip Basma', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
}

.hero-actions {
  display: flex;
  gap: 1rem;
  font-family: 'Alkatip Basma', sans-serif;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

.hero-btn {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
}

.hero-image {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 400px;
    height: 400px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  }
}

.advertisements-section {
  padding: 1rem 0 100px 0;
  background: transparent;
  margin: 0 -2rem;
  padding: 1rem 2rem 100px 2rem;
  position: relative;
}

.featured-books {
  padding: 4rem 0;

  .section-title {
    text-align: center;
    margin-bottom: 2rem;
  }
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.loading-indicators {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.skeleton-card {
  background-color: var(--secondary-color);
  border-radius: 0.5rem;
  height: 320px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

.no-books-message {
  text-align: center;
  color: #6b7280;
  padding: 2rem 0;
}

.view-all-link {
  text-align: center;
  margin-top: 2rem;
}

.features-section {
  padding: 4rem 0;
  background-color: var(--secondary-color);
  margin: 0 -2rem;
  padding: 4rem 2rem;

  .section-title {
    color: var(--text-color);
    text-align: center;
    margin-bottom: 3rem;
  }
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.feature-card {
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-5px);
  }
}

.feature-icon {
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
}

.feature-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 0.75rem;
}

.feature-description {
  font-size: 0.875rem;
  color: #6b7280;
}

.pricing-cta {
  padding: 6rem 0;
  text-align: center;

  .cta-content {
    max-width: 600px;
    margin: 0 auto;
  }
}

.cta-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 1rem;
}

.cta-description {
  font-size: 1.125rem;
  color: #6b7280;
  margin-bottom: 2rem;
}
</style>